import { useEffect, useState, type ReactNode } from 'react';
import {
  AppBar,
  Box,
  Chip,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation } from 'react-router-dom';
import { findNavigationItem } from '@/routes/navigationItems';
import { NavigationDrawer, DRAWER_WIDTH } from './NavigationDrawer';

const APP_TITLE = (import.meta.env.VITE_APP_TITLE as string | undefined) ?? 'HFI Web Console';
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api (proxy)';

type AppShellProps = {
  readonly children: ReactNode;
};

function deriveEnv(apiBase: string): { label: string; color: 'success' | 'warning' | 'info' | 'error' } {
  if (apiBase.includes('localhost') || apiBase.includes('127.0.0.1') || apiBase.startsWith('/api')) {
    return { label: 'LOCAL', color: 'info' };
  }
  if (apiBase.includes('dev')) return { label: 'DEV', color: 'success' };
  if (apiBase.includes('staging') || apiBase.includes('stg')) return { label: 'STG', color: 'warning' };
  if (apiBase.includes('prod')) return { label: 'PROD', color: 'error' };
  return { label: 'API', color: 'info' };
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const current = findNavigationItem(location.pathname);
  const env = deriveEnv(API_BASE);

  const [mobileOpen, setMobileOpen] = useState(false);

  // ルート変更時にモバイルドロワーを閉じる (NavLink クリックでも閉じるが念のため)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ minHeight: 56, gap: 1.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen((v) => !v)}
            sx={{ display: { md: 'none' }, mr: 0.5 }}
            aria-label="open navigation"
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1 }}
            >
              {APP_TITLE}
            </Typography>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ lineHeight: 1.2, mt: { xs: 0, sm: 0.25 }, fontSize: { xs: 16, sm: 20 } }}
            >
              {current?.text ?? 'Dashboard'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={`API: ${API_BASE}`} arrow>
              <Chip
                icon={<CloudIcon sx={{ fontSize: 14 }} />}
                label={env.label}
                color={env.color}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>
      <NavigationDrawer mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }} />
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
}
